import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import AICheckerResult from 'notebook/ai-tools/AICheckerResult';
import {
  CheckEntryResponseState,
  CheckEntryResultUsefulState,
  DisplayCheckEntryResults,
} from 'notebook/ai-tools/NotebookAIToolsConstants';
import {GREEN_DARK, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface AICheckerResultsProps {
  checkEntryResults: DisplayCheckEntryResults;
  onToggleCompleted: (index: number) => void;
  onToggleUsefulState: (index: number, value: CheckEntryResultUsefulState) => void;
  onRecompute: () => void;
}

/**
 * "Checklist" of results from the AI checker, or information about loading/error state.
 */
export default function AICheckerResults({
  checkEntryResults,
  onToggleCompleted,
  onToggleUsefulState,
  onRecompute,
}: AICheckerResultsProps): JSX.Element {
  if (checkEntryResults.state === CheckEntryResponseState.LOADING) {
    return <LLMLoadingMessage text={checkEntryResults.loadingState} />;
  } else if (checkEntryResults.state === CheckEntryResponseState.ERROR) {
    return (
      <div className="_test-error-message">
        <span className={css(styles.errorIcon)}>
          <BenchlingIcon icon="error" />
        </span>
        {checkEntryResults.errorMessage ?? 'An error occurred when checking the entry.'}{' '}
        <a onClick={onRecompute}>Retry</a>
      </div>
    );
  } else if (checkEntryResults.results.length === 0) {
    return (
      <div data-test-component="AICheckerResults" data-test-element="root">
        <span className={css(styles.passedIcon)}>
          <BenchlingIcon icon="check" />
        </span>
        All checks passed.
      </div>
    );
  }

  return (
    <div data-test-component="AICheckerResults" data-test-element="root">
      {checkEntryResults.results.map((result, index) => (
        <AICheckerResult
          key={`${checkEntryResults.traceId}:${index}`}
          result={result}
          onToggleCompleted={() => {
            onToggleCompleted(index);
          }}
          onToggleUseful={() => {
            onToggleUsefulState(index, CheckEntryResultUsefulState.USEFUL);
          }}
          onToggleNotUseful={() => {
            onToggleUsefulState(index, CheckEntryResultUsefulState.NOT_USEFUL);
          }}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  passedIcon: {
    color: GREEN_DARK,
    marginRight: 4,
  },
  errorIcon: {
    color: RED,
    marginRight: 4,
  },
});
